// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Rafael Pereira Satyro

import { useCallback, useEffect, useRef, useState } from 'react';
import { requestOverviewApi } from '../api/requestOverviewApi';
import type { PageResponse } from '../types/relationship';
import type {
  ProfileRequestOverviewDTO,
  RequestOverviewDisplayStatus,
  RequestOverviewFilterOptionDTO,
  RequestOverviewQueryParams,
} from '../types/requestOverview';

const DEFAULT_REFETCH_INTERVAL = 5 * 60 * 1000; // 5 minutes
const FEATURE_DISABLED_STATUS = 404;

interface UseRequestOverviewOptions extends RequestOverviewQueryParams {
  refetchInterval?: number | false;
  skip?: boolean;
}

interface UseRequestOverviewReturn {
  data: PageResponse<ProfileRequestOverviewDTO> | null;
  loading: boolean;
  error: string | null;
  /** True when the backend feature flag is off (404 from the endpoint). */
  featureDisabled: boolean;
  refetch: () => Promise<void>;
}

/**
 * Fetches the dashboard "Request Overview" page. Mirrors the existing
 * `useDashboardAlerts` shape (manual `useState`/`useEffect`/`useCallback`,
 * jittered refetch interval) — no React Query in this codebase.
 */
export function useRequestOverview(
  options: UseRequestOverviewOptions = {},
): UseRequestOverviewReturn {
  const {
    search,
    requestType,
    displayStatus,
    providerId,
    policyTypeId,
    viewAll,
    page,
    size,
    refetchInterval = DEFAULT_REFETCH_INTERVAL,
    skip = false,
  } = options;

  const [data, setData] = useState<PageResponse<ProfileRequestOverviewDTO> | null>(null);
  const [loading, setLoading] = useState(!skip);
  const [error, setError] = useState<string | null>(null);
  const [featureDisabled, setFeatureDisabled] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Stable JSON keys so useCallback only changes when filter contents change,
  // not when the caller re-creates the array literal on each render.
  const requestTypeKey = JSON.stringify(requestType ?? []);
  const displayStatusKey = JSON.stringify(displayStatus ?? []);
  const providerIdKey = JSON.stringify(providerId ?? []);
  const policyTypeIdKey = JSON.stringify(policyTypeId ?? []);

  const fetchData = useCallback(
    async (isBackground = false) => {
      try {
        if (!isBackground) setLoading(true);
        setError(null);
        const response = await requestOverviewApi.list({
          search,
          requestType: JSON.parse(requestTypeKey),
          displayStatus: JSON.parse(displayStatusKey),
          providerId: JSON.parse(providerIdKey),
          policyTypeId: JSON.parse(policyTypeIdKey),
          viewAll,
          page,
          size,
        });
        setData(response);
        setFeatureDisabled(false);
      } catch (err) {
        const status = (err as { response?: { status?: number } })?.response?.status;
        if (status === FEATURE_DISABLED_STATUS) {
          setFeatureDisabled(true);
          setData(null);
        } else {
          console.error('Failed to fetch request overview:', err);
          setError(err instanceof Error ? err.message : 'Failed to load request overview');
        }
      } finally {
        if (!isBackground) setLoading(false);
      }
    },
    [search, requestTypeKey, displayStatusKey, providerIdKey, policyTypeIdKey, viewAll, page, size],
  );

  useEffect(() => {
    if (!skip) fetchData(false);
  }, [fetchData, skip]);

  useEffect(() => {
    if (refetchInterval && !skip && !featureDisabled) {
      intervalRef.current = setInterval(() => fetchData(true), refetchInterval);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [fetchData, refetchInterval, skip, featureDisabled]);

  return { data, loading, error, featureDisabled, refetch: () => fetchData(false) };
}

export type SearchableFilterKey = 'providers' | 'policyTypes' | 'requestTypes';

interface UseRequestOverviewFilterOptionsReturn {
  providers: RequestOverviewFilterOptionDTO[];
  policyTypes: RequestOverviewFilterOptionDTO[];
  requestTypes: string[];
  displayStatuses: RequestOverviewDisplayStatus[];
  loading: boolean;
  error: string | null;
  /**
   * Refetch a single searchable filter key with the given search term.
   * Cancels any previous in-flight request for that key so out-of-order
   * responses cannot overwrite a fresher result.
   */
  refetch: (key: SearchableFilterKey, search: string) => Promise<void>;
}

/**
 * Batched fetch of the four filter dropdowns the Request Overview surfaces.
 * Each list is scoped to "values that appear on at least one in-scope
 * non-DRAFT request" so the user can never select a filter combination
 * that produces zero rows by accident.
 *
 * For the searchable filters (providers / policyTypes / requestTypes) the
 * caller can refetch with a `search` term so the in-popover search input
 * collapses the list server-side.
 */
export function useRequestOverviewFilterOptions(
  viewAll = false,
  skip = false,
): UseRequestOverviewFilterOptionsReturn {
  const [providers, setProviders] = useState<RequestOverviewFilterOptionDTO[]>([]);
  const [policyTypes, setPolicyTypes] = useState<RequestOverviewFilterOptionDTO[]>([]);
  const [requestTypes, setRequestTypes] = useState<string[]>([]);
  const [displayStatuses, setDisplayStatuses] = useState<RequestOverviewDisplayStatus[]>([]);
  const [loading, setLoading] = useState(!skip);
  const [error, setError] = useState<string | null>(null);

  // One AbortController per searchable filter key so refetching providers
  // does not cancel an in-flight policyTypes request and vice versa.
  const abortControllers = useRef<Record<SearchableFilterKey, AbortController | null>>({
    providers: null,
    policyTypes: null,
    requestTypes: null,
  });

  useEffect(() => {
    if (skip) return;
    let cancelled = false;
    // Snapshot the ref once so the cleanup function does not read it after
    // the component has unmounted (eslint react-hooks/exhaustive-deps).
    const controllersAtMount = abortControllers.current;
    setLoading(true);
    Promise.all([
      requestOverviewApi.getProviderOptions(viewAll),
      requestOverviewApi.getPolicyTypeOptions(viewAll),
      requestOverviewApi.getRequestTypeOptions(viewAll),
      requestOverviewApi.getDisplayStatusOptions(viewAll),
    ])
      .then(([providerOpts, policyTypeOpts, requestTypeOpts, displayStatusOpts]) => {
        if (cancelled) return;
        setProviders(providerOpts);
        setPolicyTypes(policyTypeOpts);
        setRequestTypes(requestTypeOpts);
        setDisplayStatuses(displayStatusOpts);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        // 404 = feature flag off; quietly fall back to empty options so the
        // dropdowns render disabled rather than surfacing a scary error.
        const status = (e as { response?: { status?: number } })?.response?.status;
        if (status === FEATURE_DISABLED_STATUS) {
          setProviders([]);
          setPolicyTypes([]);
          setRequestTypes([]);
          setDisplayStatuses([]);
        } else {
          setError(e instanceof Error ? e.message : 'Failed to load filter options');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      // Cancel any in-flight per-key refetches when the consumer unmounts.
      Object.values(controllersAtMount).forEach((c) => c?.abort());
    };
  }, [viewAll, skip]);

  const refetch = useCallback(
    async (key: SearchableFilterKey, search: string) => {
      // Cancel any previous in-flight request for this key. Out-of-order
      // responses (slow keystroke #1 finishing after fresh keystroke #2)
      // would otherwise overwrite the fresher result.
      abortControllers.current[key]?.abort();
      const controller = new AbortController();
      abortControllers.current[key] = controller;

      try {
        if (key === 'providers') {
          const opts = await requestOverviewApi.getProviderOptions(viewAll, search, controller.signal);
          if (!controller.signal.aborted) setProviders(opts);
        } else if (key === 'policyTypes') {
          const opts = await requestOverviewApi.getPolicyTypeOptions(viewAll, search, controller.signal);
          if (!controller.signal.aborted) setPolicyTypes(opts);
        } else if (key === 'requestTypes') {
          const opts = await requestOverviewApi.getRequestTypeOptions(viewAll, search, controller.signal);
          if (!controller.signal.aborted) setRequestTypes(opts);
        }
      } catch (e: unknown) {
        // Aborts are expected when the user types fast — swallow them.
        if (controller.signal.aborted) return;
        const status = (e as { response?: { status?: number } })?.response?.status;
        if (status !== FEATURE_DISABLED_STATUS) {
          console.error(`Failed to refetch filter options for ${key}:`, e);
        }
      }
    },
    [viewAll],
  );

  return { providers, policyTypes, requestTypes, displayStatuses, loading, error, refetch };
}
